import {
	EventIdentifier,
	type GuardStatus,
	noop,
	TrackingEventType,
	useAnalytics,
	UserCredentials,
	type UserRequirement,
	useSettings
} from '@capsicum/common';
import { ModalService, useRouter } from '@capsicum/common-web';
import { useRequirement } from '@capsicum/features/regulations/shared';
import { WCPledgeStatus } from '@capsicum/features/world-championship-pledge/shared';
import { RequirementsPageCredentialsNeeded, worldChampionshipTutorialAtom } from '@capsicum/socios-core';
import { useCredentialsHandler, useDeniedRequirementsModal } from '@capsicum/socios-core-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { WCPledgeRequirementModal } from '../ui/WCPledgeRequirementModal';

import { wcPledgeAcceptedRequirementAtom } from './useWCPledgeRequirementsGuard';

export const useWCPledgeRequirementsModal = (status: WCPledgeStatus) => {
	const { push } = useRouter();
	const analytics = useAnalytics();
	const hasSeenTutorial = useAtomValue(worldChampionshipTutorialAtom);
	const { showDeniedRequirementModal } = useDeniedRequirementsModal({ isLogoutModal: true, onDismiss: noop });
	const setWCPledgeAcceptedOnDirectRoute = useSetAtom(wcPledgeAcceptedRequirementAtom);

	const {
		settings: { repeatFtoDisclaimer }
	} = useSettings();

	const { requirement } = useRequirement({
		requirementIdentifier: 'fto_crypto_disclaimer',
		showDeniedRequirementModal
	});

	const { handleCredential, status: credentialStatus } = useCredentialsHandler({
		credentialCodes: [UserCredentials.ACCESS_LOCKER_ROOM],
		onCredentialGranted: () => onCredentialGranted(credentialStatus),
		page: RequirementsPageCredentialsNeeded.worldChampionshipPledge
	});

	const navigateToWCPledge = useCallback(() => {
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
			action: 'wc_pledge_banner_click'
		});
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.ButtonClicked, {
			action: 'wc_pledge_tokens_view'
		});

		setWCPledgeAcceptedOnDirectRoute(true);
		push(
			hasSeenTutorial ? '/locker-room-internationals?empty_basket=true' : '/locker-room-internationals/tutorial'
		);
	}, [analytics, push, hasSeenTutorial]);

	const onCredentialGranted = useCallback(
		(initialCredentialsStatus: GuardStatus) => {
			if (repeatFtoDisclaimer && initialCredentialsStatus !== 'denied') {
				ModalService.addModal({
					detached: true,
					renderComponent: ({ close }) => (
						<WCPledgeRequirementModal
							closeModal={close}
							requirement={requirement as UserRequirement}
							testID="wc-pledge-disclaimer"
							onAgree={navigateToWCPledge}
						/>
					),
					size: 'large'
				});

				return;
			}

			navigateToWCPledge();
		},
		[repeatFtoDisclaimer, requirement, navigateToWCPledge]
	);

	const onJoinWCPledge = useCallback(
		(e?: React.MouseEvent<HTMLDivElement>) => {
			e?.stopPropagation();
			if (status !== WCPledgeStatus.OPENED) {
				return;
			}

			handleCredential();
		},
		[status, handleCredential]
	);

	return {
		onJoinWCPledge,
		requirement
	};
};
